import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit3, Plus, Loader2, MoreVertical, Trash2, RotateCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  listChecklistItems,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  softDeleteChecklistItem,
  reactivateChecklistItem,
  type ChecklistItem as ApiChecklistItem,
} from "@/integrations/backend/checklist-items";
import { listAssessments, type Assessment } from "@/integrations/backend/assessments";
import { listLessons, type LessonRow } from "@/integrations/backend/lessons";
import { motivationOptions, labels } from "@/lib/onboarding-options";

type ChecklistType = "onboarding" | "lesson" | "assessment";

export default function ChecklistView() {
  const queryClient = useQueryClient();

  const checklistKeyTrue = queryKeys.checklistItems.admin(true);
  const checklistQuery = useQuery({
    queryKey: checklistKeyTrue,
    queryFn: () => listChecklistItems(true),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...checklistKeyTrue]),
  });

  const assessmentsKey = queryKeys.assessments.adminList(false);
  const assessmentsQuery = useQuery({
    queryKey: assessmentsKey,
    queryFn: () => listAssessments(),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...assessmentsKey]),
  });

  const lessonsKey = queryKeys.lessons.list(true);
  const lessonsQuery = useQuery({
    queryKey: lessonsKey,
    queryFn: () => listLessons(true),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...lessonsKey]),
  });

  const items = useMemo(() => {
    const rows = checklistQuery.data ?? [];
    return [...rows].sort((a, b) => a.order - b.order);
  }, [checklistQuery.data]);

  const assessments = assessmentsQuery.data ?? [];
  const lessons = lessonsQuery.data ?? [];

  const loading = checklistQuery.isLoading || assessmentsQuery.isLoading || lessonsQuery.isLoading;
  const loadError =
    (checklistQuery.error && (checklistQuery.error as Error).message) ||
    (assessmentsQuery.error && (assessmentsQuery.error as Error).message) ||
    (lessonsQuery.error && (lessonsQuery.error as Error).message) ||
    null;
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [itemToReactivate, setItemToReactivate] = useState<string | null>(null);
  const { toast } = useToast();

  type FormState = {
    name: string;
    description: string;
    type: ChecklistType;
    isConditional: boolean;
    conditionMotivations: string[]; // motivation codes
    assessmentId: string | null;
    lessonId: string | null;
    order: number;
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    name: "",
    description: "",
    type: "onboarding",
    isConditional: false,
    conditionMotivations: [],
    assessmentId: null,
    lessonId: null,
    order: 1,
  });

  const isEditing = Boolean(editingId);

  const activeLessons = lessons.filter((l) => !l.is_disabled);
  const selectedLesson = form.lessonId ? lessons.find((l) => l.id === form.lessonId) : null;
  const selectedLessonIsDisabled = Boolean(selectedLesson?.is_disabled);

  const refreshItems = async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: ["checklist-items", "admin"] });
  };

  const openCreate = () => {
    setEditingId(null);
    // Calculate default order: max order + 1
    const maxOrder = items.length > 0 ? Math.max(...items.map((i) => i.order), 0) : 0;
    setForm({
      name: "",
      description: "",
      type: "onboarding",
      isConditional: false,
      conditionMotivations: [],
      assessmentId: null,
      lessonId: null,
      order: maxOrder + 1,
    });
    setIsDialogOpen(true);
  };

  const openEdit = (item: ApiChecklistItem) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description,
      type: item.type as ChecklistType,
      isConditional: item.is_conditional,
      conditionMotivations: item.conditions || [],
      assessmentId: item.assessment_id ?? null,
      lessonId: item.lesson_id ?? null,
      order: item.order,
    });
    setIsDialogOpen(true);
  };

  const saveItem = async () => {
    if (!form.name.trim()) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (isEditing && editingId) {
        await updateChecklistItem(editingId, {
          name: form.name.trim(),
          description: form.description.trim(),
          type: form.type,
          is_conditional: form.isConditional,
          conditions: form.isConditional ? form.conditionMotivations : [],
          assessment_id: form.type === "assessment" ? form.assessmentId : null,
          lesson_id: form.type === "lesson" ? form.lessonId : null,
          order: form.order,
        });
      } else {
        await createChecklistItem({
          name: form.name.trim(),
          description: form.description.trim(),
          type: form.type,
          is_conditional: form.isConditional,
          conditions: form.isConditional ? form.conditionMotivations : [],
          assessment_id: form.type === "assessment" ? form.assessmentId : null,
          lesson_id: form.type === "lesson" ? form.lessonId : null,
          order: form.order,
        });
      }
      await refreshItems();
      setIsDialogOpen(false);
      setEditingId(null);
      toast({ title: isEditing ? "Checklist item updated" : "Checklist item created" });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to save checklist item";
      setError(errorMessage);
      toast({ title: "Save failed", description: errorMessage, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await softDeleteChecklistItem(itemToDelete);
      await refreshItems();
      toast({ title: "Checklist item deleted" });
    } catch (err: any) {
      toast({ title: "Delete failed", description: String(err?.message || err), variant: "destructive" });
    } finally {
      setItemToDelete(null);
    }
  };

  const handleReactivate = async () => {
    if (!itemToReactivate) return;
    try {
      await reactivateChecklistItem(itemToReactivate);
      await refreshItems();
      toast({ title: "Checklist item reactivated" });
    } catch (err: any) {
      toast({ title: "Reactivate failed", description: String(err?.message || err), variant: "destructive" });
    } finally {
      setItemToReactivate(null);
    }
  };

  const readableType = (t: string) => {
    if (t === "onboarding") return "Onboarding";
    if (t === "lesson") return "Lesson";
    if (t === "assessment") return "Assessment";
    return t;
  };

  const getMotivationDisplayNames = (codes: string[]): string => {
    return codes.map((code) => labels.motivation[code] || code).join(", ");
  };

  const filteredItems = items.filter((item) => {
    return showDeleted ? item.is_deleted : !item.is_deleted;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Checklist Items</h2>
          <p className="text-gray-600">Add and edit checklist items used across onboarding, lessons, and assessments.</p>
        </div>
        <Button onClick={openCreate} disabled={loading}>
          <Plus className="w-4 h-4 mr-2" />
          Add Checklist Item
        </Button>
      </div>

      <div className="flex items-center gap-4 border-b border-gray-200 pb-2">
        <button
          type="button"
          onClick={() => setShowDeleted(false)}
          className={`px-4 py-2 font-medium transition-colors ${
            !showDeleted
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Active Checklist Items
        </button>
        <button
          type="button"
          onClick={() => setShowDeleted(true)}
          className={`px-4 py-2 font-medium transition-colors ${
            showDeleted
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Deleted Checklist Items
        </button>
      </div>

      {(error || loadError) && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-800">{error || loadError}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Order</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.order}</TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{readableType(item.type)}</Badge>
                    </TableCell>
                    <TableCell>
                      {item.is_conditional
                        ? (item.conditions.length ? getMotivationDisplayNames(item.conditions) : "-")
                        : "All users"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white text-black">
                          {!showDeleted ? (
                            <>
                              <DropdownMenuItem onClick={() => openEdit(item)}>
                                <Edit3 className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setItemToDelete(item.id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => setItemToReactivate(item.id)}
                              className="text-green-600 focus:text-green-600"
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Reactivate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                  {filteredItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-sm text-gray-500">
                        {showDeleted ? "No deleted checklist items." : "No checklist items yet."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogScrollContent className="bg-white text-black">
          <DialogHeader className="shrink-0">
            <DialogTitle>{isEditing ? "Edit Checklist Item" : "Add Checklist Item"}</DialogTitle>
            <DialogDescription>Define the item details and optional condition.</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="chk-name">Name</Label>
              <Input
                id="chk-name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Complete Onboarding"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="chk-desc">Description</Label>
              <Textarea
                id="chk-desc"
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Short description of this item"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="chk-order">Order</Label>
              <Input
                id="chk-order"
                type="number"
                min="1"
                value={form.order}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0) {
                    setForm((prev) => ({ ...prev, order: val }));
                  }
                }}
                placeholder="Order"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((prev) => ({ ...prev, type: v as ChecklistType, assessmentId: v === "assessment" ? prev.assessmentId : null, lessonId: v === "lesson" ? prev.lessonId : null }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="lesson">Lesson</SelectItem>
                  <SelectItem value="assessment">Assessment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type === "assessment" && (
              <div className="space-y-2">
                <Label>Assessment</Label>
                <Select
                  value={form.assessmentId ?? "__none__"}
                  onValueChange={(v) => setForm((prev) => ({ ...prev, assessmentId: v === "__none__" ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an assessment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {assessments.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.type === "lesson" && (
              <div className="space-y-2">
                <Label>Lesson</Label>
                <Select
                  value={form.lessonId ?? "__none__"}
                  onValueChange={(v) => setForm((prev) => ({ ...prev, lessonId: v === "__none__" ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a lesson" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {selectedLessonIsDisabled && selectedLesson && (
                      <>
                        <SelectSeparator />
                        <SelectItem value={selectedLesson.id} disabled>
                          {selectedLesson.title} (Deactivated)
                        </SelectItem>
                      </>
                    )}
                    <SelectSeparator />
                    {activeLessons.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="conditional-toggle" className="cursor-pointer">Conditional for specific motivations?</Label>
                <Switch
                  id="conditional-toggle"
                  checked={form.isConditional}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({
                      ...prev,
                      isConditional: Boolean(checked),
                      conditionMotivations: checked ? prev.conditionMotivations : [],
                    }))
                  }
                />
              </div>
              {form.isConditional && (
                <div className="space-y-2">
                  <Label>Motivations</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {motivationOptions.map((m) => {
                      const selected = form.conditionMotivations.includes(m.id);
                      return (
                        <label key={m.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={selected}
                            onCheckedChange={(checked) =>
                              setForm((prev) => {
                                const next = new Set(prev.conditionMotivations);
                                if (checked) next.add(m.id);
                                else next.delete(m.id);
                                return { ...prev, conditionMotivations: Array.from(next) };
                              })
                            }
                          />
                          <span>{m.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter className="shrink-0">
            <Button variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900" onClick={() => setIsDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={saveItem} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogScrollContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={Boolean(itemToDelete)} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent className="bg-white text-black">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Checklist Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete the checklist item. You can reactivate it later from the Deleted Checklist Items tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate Confirmation Dialog */}
      <AlertDialog open={Boolean(itemToReactivate)} onOpenChange={(open) => !open && setItemToReactivate(null)}>
        <AlertDialogContent className="bg-white text-black">
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate Checklist Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the checklist item and make it available again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReactivate} className="bg-green-600 text-white hover:bg-green-700">
              Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


